import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, MailCheck } from 'lucide-react';
import { Button } from './ui/button';
import { API_URL } from '../utils/api';
import { getErrorMessage } from '../utils/errors';

const RESEND_COOLDOWN_SECONDS = 30;

/**
 * "We sent you a link" message with a resend button, shown after an
 * email-and-password signup and when an unconfirmed account tries to sign in.
 * `sendFailed` is set when the signup email couldn't be sent, so the page
 * leads with "send it again" instead of claiming a link is on its way.
 */
export default function CheckEmailNotice({ email, intro, sendFailed = false }) {
  const [failed, setFailed] = useState(sendFailed);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const resend = async () => {
    setSending(true);
    try {
      await axios.post(`${API_URL}/auth/resend-verification`, { email });
      toast.success('A new link is on its way. Please check your email.');
      setFailed(false);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      toast.error(getErrorMessage(err, 'We could not send the email. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="text-center space-y-4" role="status" data-testid="check-email-notice">
      <MailCheck className="h-10 w-10 mx-auto text-gold" aria-hidden="true" />
      {failed ? (
        <p className="text-ink" data-testid="verification-send-failed">
          Your account is created, but we couldn't send the confirmation email to <strong>{email}</strong> just now.
          Press the button below to send it again.
        </p>
      ) : (
        <p className="text-ink">
          {intro || 'We sent a link to'} <strong>{email}</strong>. Click it to confirm your email and finish signing up.
        </p>
      )}
      <p className="text-sm text-ink/60">The link works for 24 hours. Check your spam folder if you don't see it.</p>
      <Button
        type="button"
        variant="outline"
        onClick={resend}
        disabled={sending || cooldown > 0}
        className="w-full h-10"
        data-testid="resend-verification"
      >
        {sending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {cooldown > 0 ? `Email sent. Resend in ${cooldown}s` : failed ? 'Send the email again' : 'Resend the email'}
      </Button>
    </div>
  );
}
